/**
 * `/admin` → **Transcripts**: root reads somebody's assistant chats.
 *
 * Three panes, left to right, the way a mail client reads: the people who have
 * chats, the chats of the one picked, and the transcript of the chat picked.
 * Picking in one pane never moves or clears anything to its left, so root keeps
 * their place while reading through somebody's history.
 *
 * Root only, on the server as well as here (`transcripts_router.py`), and never
 * widened by a permission - see `backend/assistant/transcripts.py`. Opening a
 * transcript is recorded in Activity by the server; the header says so, because
 * access that is accountable should not be accountable in secret.
 *
 * Deleted chats are listed and readable, greyed and marked: root asked for them,
 * because an answer that was reported is still worth reading after the person
 * tidied their list.
 *
 * An answer somebody marked "Not what I asked for" is marked where it sits, on
 * the person, on the chat and on the turn, and the chats can be narrowed to
 * those. **Usage** beside **Chats** is the numbers: questions and dislikes per
 * day and per person (`transcripts-usage.ts`).
 *
 * The transcript reuses the assistant's own bubbles, Markdown and proposal-card
 * classes, so it reads like the chat the person saw. **Technical details** adds
 * what they did not see: model, latency, tokens, cost, reasoning, and every tool
 * call with its input, result and error.
 */

import { html, nothing, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { LightElement } from '../../lit-base.ts';
import { StoreController } from '../../state/controller.ts';
import { catalogAvailabilityKnown, catalogAvailable, isRootUser } from '../../state/store.ts';
import { getTranscript, listTranscriptChats, listTranscriptPeople } from '../../api/endpoints.ts';
import type {
  Transcript,
  TranscriptChat,
  TranscriptOwner,
  TranscriptPerson,
  TranscriptToolCall,
  TranscriptTurn,
} from '../../api/types.ts';
import { renderMarkdown } from '../assistant/markdown.ts';
import { exportHeadline, plotHeadline } from '../assistant/humanize.ts';
import { shipLabel } from '../../utils/ships.ts';
import { timeAgo } from '../../utils/dates.ts';
import './transcripts-usage.ts';
import { userMessage } from '../../api/errors.ts';

const TECHNICAL_KEY = 'neptune_admin_transcripts_technical';
const VIEW_KEY = 'neptune_admin_transcripts_view';

type View = 'chats' | 'usage';

function readView(): View {
  try {
    return localStorage.getItem(VIEW_KEY) === 'usage' ? 'usage' : 'chats';
  } catch {
    return 'chats';
  }
}

function writeView(view: View): void {
  try {
    localStorage.setItem(VIEW_KEY, view);
  } catch {
    /* a private window: the switch still works for this visit */
  }
}

function disliked(n: number): string {
  return `${n} disliked`;
}

const DISLIKED_TITLE = 'Answers marked "Not what I asked for"';

function readTechnical(): boolean {
  try {
    return localStorage.getItem(TECHNICAL_KEY) === '1';
  } catch {
    return false;
  }
}

function writeTechnical(on: boolean): void {
  try {
    localStorage.setItem(TECHNICAL_KEY, on ? '1' : '0');
  } catch {
    /* a private window: the switch still works for this visit */
  }
}

function dateTime(iso: string): string {
  return new Date(iso).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' });
}

function shortDate(iso: string): string {
  const date = new Date(iso);
  if (date.toDateString() === new Date().toDateString()) {
    return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  }
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function cost(usd: number | null | undefined): string {
  if (usd == null) return '-';
  return usd < 0.01 && usd > 0 ? '<$0.01' : `$${usd.toFixed(2)}`;
}

function tokens(n: number): string {
  return n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n);
}

function errorMessage(e: unknown): string {
  return userMessage(e);
}

@customElement('transcripts-panel')
export class TranscriptsPanel extends LightElement {
  private store = new StoreController(this, [isRootUser, catalogAvailable, catalogAvailabilityKnown]);

  @state() private people: TranscriptPerson[] = [];
  @state() private peopleQuery = '';
  @state() private peopleLoading = false;
  @state() private peopleError = '';

  @state() private personId: string | null = null;
  @state() private owner: TranscriptOwner | null = null;
  @state() private chats: TranscriptChat[] = [];
  @state() private chatQuery = '';
  /** Only chats with an answer marked "Not what I asked for". */
  @state() private flaggedOnly = false;
  @state() private chatsLoading = false;
  @state() private chatsError = '';

  @state() private chatId: string | null = null;
  @state() private transcript: Transcript | null = null;
  @state() private transcriptLoading = false;
  @state() private transcriptError = '';

  @state() private technical = readTechnical();
  @state() private view: View = readView();

  private requested = false;
  /** One counter per pane: a slow answer for an earlier pick is dropped. */
  private peopleRev = 0;
  private chatsRev = 0;
  private transcriptRev = 0;
  private peopleTimer: number | undefined;
  private chatsTimer: number | undefined;

  override willUpdate(): void {
    if (!this.requested && isRootUser.value && catalogAvailabilityKnown.value && catalogAvailable.value) {
      this.requested = true;
      void this.loadPeople();
    }
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    window.clearTimeout(this.peopleTimer);
    window.clearTimeout(this.chatsTimer);
    this.peopleRev++;
    this.chatsRev++;
    this.transcriptRev++;
  }

  private async loadPeople(): Promise<void> {
    const rev = ++this.peopleRev;
    this.peopleLoading = true;
    this.peopleError = '';
    try {
      const { people } = await listTranscriptPeople(this.peopleQuery);
      if (rev === this.peopleRev) this.people = people;
    } catch (e) {
      if (rev === this.peopleRev) this.peopleError = errorMessage(e);
    } finally {
      if (rev === this.peopleRev) this.peopleLoading = false;
    }
  }

  private async loadChats(): Promise<void> {
    const id = this.personId;
    if (!id) return;
    const rev = ++this.chatsRev;
    this.chatsLoading = true;
    this.chatsError = '';
    try {
      const { owner, chats } = await listTranscriptChats(id, this.chatQuery, this.flaggedOnly);
      if (rev === this.chatsRev) {
        this.owner = owner;
        this.chats = chats;
      }
    } catch (e) {
      if (rev === this.chatsRev) this.chatsError = errorMessage(e);
    } finally {
      if (rev === this.chatsRev) this.chatsLoading = false;
    }
  }

  private async loadTranscript(): Promise<void> {
    const id = this.chatId;
    if (!id) return;
    const rev = ++this.transcriptRev;
    this.transcriptLoading = true;
    this.transcriptError = '';
    try {
      const transcript = await getTranscript(id);
      if (rev === this.transcriptRev) this.transcript = transcript;
    } catch (e) {
      if (rev === this.transcriptRev) this.transcriptError = errorMessage(e);
    } finally {
      if (rev === this.transcriptRev) this.transcriptLoading = false;
    }
  }

  private pickPerson(person: TranscriptOwner): void {
    if (person.id === this.personId) return;
    this.personId = person.id;
    this.owner = { id: person.id, display_name: person.display_name, email: person.email };
    this.chats = [];
    this.chatQuery = '';
    this.flaggedOnly = false;
    // A transcript belongs to one person; leaving it up beside someone else's
    // list would invite reading it as theirs.
    this.chatId = null;
    this.transcript = null;
    this.transcriptError = '';
    this.transcriptRev++;
    void this.loadChats();
  }

  private pickChat(chat: TranscriptChat): void {
    if (chat.id === this.chatId) return;
    this.chatId = chat.id;
    this.transcript = null;
    void this.loadTranscript();
  }

  private onPeopleSearch(value: string): void {
    this.peopleQuery = value;
    this.peopleRev++;
    window.clearTimeout(this.peopleTimer);
    this.peopleTimer = window.setTimeout(() => void this.loadPeople(), 300);
  }

  private onChatSearch(value: string): void {
    this.chatQuery = value;
    this.chatsRev++;
    window.clearTimeout(this.chatsTimer);
    this.chatsTimer = window.setTimeout(() => void this.loadChats(), 300);
  }

  private setFlaggedOnly(on: boolean): void {
    this.flaggedOnly = on;
    void this.loadChats();
  }

  private setView(view: View): void {
    this.view = view;
    writeView(view);
  }

  /** Usage → a person's row: read their chats. */
  private openPerson(person: TranscriptOwner): void {
    this.setView('chats');
    this.pickPerson(person);
  }

  private setTechnical(on: boolean): void {
    this.technical = on;
    writeTechnical(on);
  }

  /* ── panes ──────────────────────────────────────────────── */

  private renderPeople(): TemplateResult {
    return html`<section class="transcripts-pane transcripts-people" aria-label="People with chats">
      <div class="transcripts-pane-head">
        <input class="catalog-search" type="search" placeholder="Find a person…" aria-label="Find a person"
          maxlength="100" .value=${this.peopleQuery}
          @input=${(e: Event) => this.onPeopleSearch((e.target as HTMLInputElement).value)} />
      </div>
      <div class="transcripts-list" aria-busy=${this.peopleLoading}>
        ${this.peopleError
          ? html`<p class="transcripts-note is-error" role="alert">${this.peopleError}</p>`
          : !this.people.length
            ? html`<p class="transcripts-note">${this.peopleLoading ? 'Loading…'
                : this.peopleQuery ? 'Nobody matches.' : 'Nobody has chatted with SONAR yet.'}</p>`
            : repeat(this.people, (p) => p.id, (p) => html`
                <button class="transcripts-item ${p.id === this.personId ? 'active' : ''}"
                  aria-current=${p.id === this.personId ? 'true' : 'false'} @click=${() => this.pickPerson(p)}>
                  <span class="transcripts-item-title">
                    <span class="transcripts-item-name">${p.display_name}</span>
                    <span class="transcripts-count" title=${`${p.chats} chat${p.chats === 1 ? '' : 's'}${
                      p.deleted_chats ? `, ${p.deleted_chats} deleted` : ''}`}>${p.chats + p.deleted_chats}</span>
                  </span>
                  <span class="transcripts-item-meta">
                    ${p.is_active ? nothing : html`<span class="admin-kind">inactive</span>`}
                    ${p.disliked ? html`<span class="transcripts-chip is-disliked" title=${DISLIKED_TITLE}>${disliked(p.disliked)}</span>` : nothing}
                    <time datetime=${p.last_message_at} title=${dateTime(p.last_message_at)}>${timeAgo(p.last_message_at, 'long')}</time>
                  </span>
                </button>`)}
      </div>
    </section>`;
  }

  private renderChatItem(chat: TranscriptChat): TemplateResult {
    const facts: Array<string> = [
      `${chat.turn_count} turn${chat.turn_count === 1 ? '' : 's'}`,
      ...chat.ships.slice(0, 2).map(shipLabel),
    ];
    if (chat.ships.length > 2) facts.push(`+${chat.ships.length - 2}`);
    return html`<button class="transcripts-item ${chat.id === this.chatId ? 'active' : ''} ${chat.deleted_at ? 'is-deleted' : ''}"
      aria-current=${chat.id === this.chatId ? 'true' : 'false'} @click=${() => this.pickChat(chat)}>
      <span class="transcripts-item-title">
        <span class="transcripts-item-name">${chat.title}</span>
        <time datetime=${chat.last_message_at} title=${dateTime(chat.last_message_at)}>${shortDate(chat.last_message_at)}</time>
      </span>
      <span class="transcripts-item-meta">
        <span>${facts.join(' · ')}</span>
        ${chat.plots ? html`<span class="transcripts-chip" title="Charts proposed">${chat.plots} chart${chat.plots === 1 ? '' : 's'}</span>` : nothing}
        ${chat.exports ? html`<span class="transcripts-chip" title="Exports proposed">${chat.exports} export${chat.exports === 1 ? '' : 's'}</span>` : nothing}
        ${chat.errors ? html`<span class="transcripts-chip is-error" title="Turns that failed">${chat.errors} failed</span>` : nothing}
        ${chat.disliked ? html`<span class="transcripts-chip is-disliked" title=${DISLIKED_TITLE}>${disliked(chat.disliked)}</span>` : nothing}
        ${chat.deleted_at ? html`<span class="admin-kind" title=${`Deleted ${dateTime(chat.deleted_at)}`}>deleted</span>` : nothing}
        ${chat.imported ? html`<span class="admin-kind" title="Carried over from their browser">imported</span>` : nothing}
      </span>
    </button>`;
  }

  private renderChats(): TemplateResult {
    if (!this.personId) {
      return html`<section class="transcripts-pane transcripts-chats is-empty">
        <p class="transcripts-note">Pick a person to see their chats.</p>
      </section>`;
    }
    const live = this.chats.filter((c) => !c.deleted_at).length;
    const deleted = this.chats.length - live;
    return html`<section class="transcripts-pane transcripts-chats" aria-label="Chats">
      <div class="transcripts-pane-head">
        <div class="transcripts-owner">
          <span class="transcripts-owner-name">${this.owner?.display_name}</span>
          <span class="people-muted">${this.chatsLoading && !this.chats.length ? 'Loading…'
            : `${live} chat${live === 1 ? '' : 's'}${deleted ? ` · ${deleted} deleted` : ''}`}</span>
        </div>
        <input class="catalog-search" type="search" placeholder="Search questions and answers…"
          aria-label="Search this person's chats" maxlength="100" .value=${this.chatQuery}
          @input=${(e: Event) => this.onChatSearch((e.target as HTMLInputElement).value)} />
        <label class="transcripts-technical">
          <input class="admin-switch" type="checkbox" role="switch" .checked=${this.flaggedOnly}
            @change=${(e: Event) => this.setFlaggedOnly((e.target as HTMLInputElement).checked)} />
          <span>Only chats with a disliked answer</span>
        </label>
      </div>
      <div class="transcripts-list" aria-busy=${this.chatsLoading}>
        ${this.chatsError
          ? html`<p class="transcripts-note is-error" role="alert">${this.chatsError}</p>`
          : !this.chats.length
            ? html`<p class="transcripts-note">${this.chatsLoading ? 'Loading…'
                : this.flaggedOnly ? 'No chat has a disliked answer.'
                  : this.chatQuery ? 'No chat matches.' : 'No chats.'}</p>`
            : repeat(this.chats, (c) => c.id, (c) => this.renderChatItem(c))}
      </div>
    </section>`;
  }

  private renderToolCall(call: TranscriptToolCall): TemplateResult {
    const status = call.status === 'error' ? 'error' : 'ok';
    return html`<details class="assistant-tool">
      <summary>
        <code>${call.name}</code>
        <span class="assistant-tool-status assistant-tool-status-${status}">${status}</span>
        ${call.duration_ms != null ? html`<span class="transcripts-dim">${call.duration_ms} ms</span>` : nothing}
        ${call.error ? html`<span class="transcripts-tool-error">${call.error}</span>` : nothing}
      </summary>
      <div class="assistant-tool-body">
        <div><strong>Input</strong><pre>${JSON.stringify(call.input, null, 2)}</pre></div>
        ${call.error ? html`<div><strong>Error</strong><pre>${call.error}</pre></div>` : nothing}
        ${call.result != null
          ? html`<div><strong>Result${call.result_truncated ? ' (truncated)' : ''}</strong><pre>${call.result}</pre></div>`
          : nothing}
      </div>
    </details>`;
  }

  /** What the person was shown as a card, without the button to act on it. */
  private renderProposal(kind: 'plot' | 'export', spec: object, headline: string, typeLabel: string,
    explanation: string | null | undefined): TemplateResult {
    return html`<div class="assistant-plotcard transcripts-card">
      <div class="assistant-plotcard-head">
        <span class="assistant-plotcard-title">${headline}</span>
      </div>
      <span class="assistant-plotcard-type">${typeLabel}</span>
      ${explanation ? html`<p class="assistant-plotcard-why">${explanation}</p>` : nothing}
      ${this.technical
        ? html`<details class="assistant-specs">
            <summary>the ${kind === 'plot' ? 'plot' : 'export'} it proposed</summary>
            <pre class="transcripts-json">${JSON.stringify(spec, null, 2)}</pre>
          </details>`
        : nothing}
    </div>`;
  }

  private renderTurn(turn: TranscriptTurn): TemplateResult {
    const failed = turn.status === 'error';
    const meta: string[] = [];
    if (turn.model) meta.push(turn.model);
    if (turn.latency_ms != null) meta.push(`${(turn.latency_ms / 1000).toFixed(1)} s`);
    meta.push(`${tokens(turn.input_tokens)} in / ${tokens(turn.output_tokens)} out`);
    meta.push(cost(turn.cost_usd));
    if (turn.iterations != null) meta.push(`${turn.iterations} step${turn.iterations === 1 ? '' : 's'}`);
    return html`
      <div class="transcripts-stamp">
        <time datetime=${turn.created_at}>${dateTime(turn.created_at)}</time>
        ${turn.ship_hint ? html` · ${shipLabel(turn.ship_hint)}` : nothing}
      </div>
      <div class="assistant-turn assistant-turn-user">
        <div class="assistant-bubble">${turn.user_message}</div>
      </div>
      <div class="assistant-turn assistant-turn-assistant ${turn.feedback.length ? 'is-disliked' : ''}">
        ${turn.feedback.length
          ? html`<p class="transcripts-flag is-disliked">
              Marked "Not what I asked for"
              · <time datetime=${turn.feedback[0]!.created_at}>${dateTime(turn.feedback[0]!.created_at)}</time>
              ${turn.feedback.length > 1 ? html` · ${turn.feedback.length} times` : nothing}
            </p>
            ${turn.feedback.filter((f) => f.comment).map((f) => html`<p class="transcripts-flag">"${f.comment}"</p>`)}`
          : nothing}
        ${turn.assistant_answer
          // prettier-ignore
          ? html`<div class="assistant-bubble assistant-md">${unsafeHTML(renderMarkdown(turn.assistant_answer))}</div>`
          : nothing}
        ${turn.mode === 'chief_engineer'
          ? html`<p class="transcripts-flag">Chief Engineer mode: SONAR's scope and style rules were relaxed</p>`
          : nothing}
        ${turn.declined_reason
          ? html`<p class="transcripts-flag">Declined as out of scope: ${turn.declined_reason}</p>`
          : nothing}
        ${failed
          ? html`<p class="transcripts-flag is-error">This turn failed${turn.error_type ? ` (${turn.error_type})` : ''}${
              this.technical && turn.error ? `: ${turn.error}` : '.'}</p>`
          : nothing}
        ${turn.proposed_plot
          ? this.renderProposal('plot', turn.proposed_plot, plotHeadline(turn.proposed_plot),
              turn.proposed_plot.plot_type, turn.proposed_plot.explanation)
          : nothing}
        ${turn.proposed_export
          ? this.renderProposal('export', turn.proposed_export, exportHeadline(turn.proposed_export),
              'data export', turn.proposed_export.explanation)
          : nothing}
        ${this.technical
          ? html`
              ${turn.assistant_reasoning
                ? html`<details class="assistant-reasoning">
                    <summary>how it worked this out</summary>
                    <div>${turn.assistant_reasoning}</div>
                  </details>`
                : nothing}
              ${turn.tool_calls.length
                ? html`<details class="assistant-tools" open>
                    <summary>checked ${turn.tool_calls.length} thing${turn.tool_calls.length === 1 ? '' : 's'}</summary>
                    <div class="assistant-tool-list">${turn.tool_calls.map((c) => this.renderToolCall(c))}</div>
                  </details>`
                : nothing}
              <div class="transcripts-turn-meta">${meta.join(' · ')}${turn.imported ? ' · imported' : ''}</div>`
          : nothing}
      </div>
    `;
  }

  private renderTranscript(): TemplateResult {
    if (!this.chatId) {
      return html`<section class="transcripts-pane transcripts-read is-empty">
        <p class="transcripts-note">${this.personId ? 'Pick a chat to read it.' : ''}</p>
      </section>`;
    }
    const t = this.transcript;
    return html`<section class="transcripts-pane transcripts-read" aria-label="Transcript" aria-busy=${this.transcriptLoading}>
      ${this.transcriptError
        ? html`<p class="transcripts-note is-error" role="alert">${this.transcriptError}</p>`
        : !t
          ? html`<p class="transcripts-note">Loading…</p>`
          : html`
              <header class="transcripts-read-head">
                <div class="transcripts-read-title">
                  <h2>${t.title}</h2>
                  <span class="people-muted">
                    ${t.owner.display_name} · started ${dateTime(t.started_at)} · ${t.turns.length} turn${t.turns.length === 1 ? '' : 's'}
                    · ${tokens(t.input_tokens + t.output_tokens)} tokens · ${cost(t.cost_usd)}
                  </span>
                </div>
                <label class="transcripts-technical">
                  <span>Technical details</span>
                  <input class="admin-switch" type="checkbox" role="switch" .checked=${this.technical}
                    @change=${(e: Event) => this.setTechnical((e.target as HTMLInputElement).checked)} />
                </label>
              </header>
              ${t.deleted_at
                ? html`<p class="transcripts-banner">${t.owner.display_name} deleted this chat on ${dateTime(t.deleted_at)}. It no longer appears in their History.</p>`
                : nothing}
              <div class="transcripts-conversation">
                ${t.turns.length
                  ? repeat(t.turns, (turn) => turn.id, (turn) => this.renderTurn(turn))
                  : html`<p class="transcripts-note">This chat has no turns.</p>`}
              </div>
              <p class="admin-note transcripts-recorded">Opening this chat was recorded in Activity.</p>
            `}
    </section>`;
  }

  override render(): TemplateResult {
    if (!catalogAvailabilityKnown.value) {
      return html`<div class="catalog-empty" role="status">Loading…</div>`;
    }
    if (!catalogAvailable.value) {
      return html`<div class="catalog-empty"><p class="catalog-empty-title">Transcripts need the metadata database</p>
        <p>Without it, chats stay in each person's browser and nobody else can read them.</p></div>`;
    }
    if (!isRootUser.value) {
      return html`<div class="catalog-empty"><p class="catalog-empty-title">Only Root can read other people's chats</p></div>`;
    }
    return html`<div class="transcripts-bar">
        <div class="seg" role="group" aria-label="View">
          <button type="button" class="seg-btn ${this.view === 'chats' ? 'active' : ''}"
            aria-pressed=${this.view === 'chats'} @click=${() => this.setView('chats')}>Chats</button>
          <button type="button" class="seg-btn ${this.view === 'usage' ? 'active' : ''}"
            aria-pressed=${this.view === 'usage'} @click=${() => this.setView('usage')}>Usage</button>
        </div>
      </div>
      ${this.view === 'usage'
        ? html`<transcripts-usage @open-person=${(e: CustomEvent<TranscriptOwner>) => this.openPerson(e.detail)}></transcripts-usage>`
        : html`<div class="transcripts">
            ${this.renderPeople()} ${this.renderChats()} ${this.renderTranscript()}
          </div>`}`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'transcripts-panel': TranscriptsPanel;
  }
}
